import {
  AppDataComponent,
  DocumentServicesObject,
  EditorClientSpecMapEntry,
} from '@wix/document-services-types';
import {
  AppExposure,
  AppExposureGroup,
  AppExposureItem,
  Breakpoint,
  ExposureItemType,
} from '@wix/editor-platform-host-integration-apis';
import { EditorSubType } from '@wix/editor-platform-sdk-types';
import { HttpClient, IHttpClient } from '@wix/http-client';
import { LAYOUT } from '@wix/component-protocol/schema';
import { getStructuredTemplate } from '@wix/ambassador-editor-structured-templates-v1-structured-template/http';
import {
  // The generated enum for EditorExposureGroup.type. Aliased for readability —
  // the "Api" prefix is a generator artifact; this is the group's type.
  ApiGroupType as GroupType,
  BreakpointEnumBreakpoint,
  ComponentType,
  EditorExposure,
  EditorExposureGroup,
  EditorExposureItem,
  EditorType,
} from '@wix/ambassador-devcenter-components-v1-component/types';
import { PlatformContext } from '../../../types/platformApi';
import { getCurrentBreakpoint } from '../../utils/getCurrentBreakpoint';
import { getEditorSubType } from '../../../utils';

export { GroupType };

// Maps the current editor (EditorSubType) to the EditorExposure.targetEditor
// value it corresponds to. Subtypes without an entry (EditorX, MobileStudio,
// Blocks) only match all-editors instances.
const SUBTYPE_TO_TARGET: Partial<Record<EditorSubType, EditorType>> = {
  [EditorSubType.Classic]: EditorType.CLASSIC_EDITOR_TYPE,
  [EditorSubType.Studio]: EditorType.STUDIO_EDITOR_TYPE,
  [EditorSubType.Studio2]: EditorType.STUDIO_2_EDITOR_TYPE,
  [EditorSubType.Editor3]: EditorType.HARMONY_EDITOR_TYPE,
};

// Returns every EDITOR_EXPOSURE component instance declared by the app.
const readEditorExposureInstances = (
  components: readonly AppDataComponent[],
): EditorExposure[] =>
  components
    .filter((component) => component.type === ComponentType.EDITOR_EXPOSURE)
    // EDITOR_EXPOSURE.data isn't modeled in AppDataComponent, so assert its shape.
    .map((component) => (component as { data?: EditorExposure }).data)
    .filter((data): data is EditorExposure => data !== undefined);

// Picks the single EditorExposure instance applicable to the current editor:
// an instance that explicitly targets the current editor wins; otherwise an
// instance that targets all editors (empty/absent target list) is used.
const selectExposureForEditor = (
  instances: EditorExposure[],
  currentEditor: EditorSubType | undefined,
): EditorExposure | undefined => {
  const currentTarget = currentEditor
    ? SUBTYPE_TO_TARGET[currentEditor]
    : undefined;

  const specificMatch = currentTarget
    ? instances.find((instance) =>
        (instance.targetEditor ?? []).includes(currentTarget),
      )
    : undefined;
  if (specificMatch) {
    return specificMatch;
  }

  return instances.find(
    (instance) => (instance.targetEditor ?? []).length === 0,
  );
};

// Maps the editor's current breakpoint (a LAYOUT.BREAKPOINT value) to the
// EditorExposure breakpoint enum. Both are string enums with the same members;
// only large and small are meaningful here, so anything non-small maps to large.
const toBreakpointEnum = (breakpoint: Breakpoint): BreakpointEnumBreakpoint =>
  breakpoint === LAYOUT.BREAKPOINT.small
    ? BreakpointEnumBreakpoint.small
    : BreakpointEnumBreakpoint.large;

// Whether the item is exposed at the current breakpoint. An empty or absent
// optimizedFor list means the item is exposed at every breakpoint.
const isExposedAtBreakpoint = (
  item: EditorExposureItem,
  currentBreakpoint: Breakpoint,
): boolean => {
  const optimizedFor = item.optimizedFor ?? [];
  return (
    optimizedFor.length === 0 ||
    optimizedFor.includes(toBreakpointEnum(currentBreakpoint))
  );
};

// Resolves the thumbnail to show for the item: the override matching the
// editor's current breakpoint wins, then the default, then any override URL.
const pickImage = (
  item: EditorExposureItem,
  currentBreakpoint: Breakpoint,
): string => {
  const thumbnails = item.thumbnails;
  if (!thumbnails) {
    return '';
  }
  const overrides = thumbnails.overrides ?? [];
  const match = overrides.find(
    (o) => o.breakpoint === toBreakpointEnum(currentBreakpoint),
  );
  if (match?.thumbnailUrl) {
    return match.thumbnailUrl;
  }
  if (thumbnails.defaultUrl) {
    return thumbnails.defaultUrl;
  }
  return overrides.find((o) => o.thumbnailUrl)?.thumbnailUrl ?? '';
};

// Fetches the template's required contexts from the structured-templates DM
// service — the contextType of each scope, which the editor attaches when
// adding the template. (The title comes from the composition's displayName.)
// A failed request degrades to undefined so one bad template doesn't drop the
// whole exposure result.
const fetchContextDependencies = async (
  templateId: string,
  httpClient: IHttpClient,
): Promise<string[] | undefined> => {
  try {
    const { data } = await httpClient.request(
      getStructuredTemplate({ structuredTemplateId: templateId }),
    );
    const contexts = (data.structuredTemplate?.templateScopes ?? [])
      .map((scope) => scope.contextType)
      .filter((contextType): contextType is string => !!contextType);
    return contexts.length ? contexts : undefined;
  } catch {
    return undefined;
  }
};

// The subset of a ClientSpecMap component the mappers read: a custom element's
// display name (data.base.name / name) and an ERC's manifest component-type
// (data.type).
type CsmComponent = {
  name?: string;
  data?: { type?: string; base?: { name?: string } };
};

// Per-app dependencies the item mappers need to resolve titles and metadata.
// Built once per app in buildEditorExposureForApp and threaded down. The app's
// CSM components are read once here rather than per item.
interface AppItemContext {
  components: readonly AppDataComponent[];
  context: PlatformContext;
  httpClient: IHttpClient;
  currentBreakpoint: Breakpoint;
}

// Finds the app's ClientSpecMap component for a given extension, matching on
// componentId. Shared by the custom-element and ERC mappers.
const findComponent = (
  { components }: AppItemContext,
  extensionId: string,
): CsmComponent | undefined =>
  // The name/data.type/data.base fields we read aren't modeled per union
  // member, so assert the shape after matching on componentId.
  components.find((c) => c.componentId === extensionId) as
    | CsmComponent
    | undefined;

const toAppExposureItem = async (
  item: EditorExposureItem,
  appItem: AppItemContext,
): Promise<AppExposureItem | null> => {
  // Items not exposed at the current breakpoint are dropped from the group.
  if (!isExposedAtBreakpoint(item, appItem.currentBreakpoint)) {
    return null;
  }

  const image = pickImage(item, appItem.currentBreakpoint);

  if (item.customElement) {
    // extensionId is optional in the generated type but always present here.
    const extensionId = item.customElement.extensionId!;
    const component = findComponent(appItem, extensionId);
    return {
      type: ExposureItemType.CUSTOM_ELEMENT,
      id: extensionId,
      title: component?.data?.base?.name ?? component?.name ?? '',
      image,
    };
  }

  if (item.editorReactComponent) {
    // extensionId is optional in the generated type but always present here.
    const extensionId = item.editorReactComponent.extensionId!;
    // The ERC's manifest component-type is the CSM component's data.type; the
    // title is that type's manifest display name.
    const componentType = findComponent(appItem, extensionId)?.data?.type;
    const title = componentType
      ? appItem.context.componentsPlatformService.manifest.getDisplayNameByComponentType(
          componentType,
        ) ?? ''
      : '';
    return {
      type: ExposureItemType.EDITOR_REACT_COMPONENT,
      id: extensionId,
      title,
      image,
      presetId: item.editorReactComponent.presetId ?? undefined,
      componentType,
    };
  }

  if (item.composition) {
    // templateId is optional in the generated type but always present here.
    const templateId = item.composition.templateId!;
    return {
      type: ExposureItemType.TEMPLATE,
      id: templateId,
      title: item.composition.displayName ?? '',
      image,
      contextDependencies: await fetchContextDependencies(
        templateId,
        appItem.httpClient,
      ),
    };
  }

  return null;
};

const toAppExposureGroup = async (
  group: EditorExposureGroup,
  appItem: AppItemContext,
): Promise<AppExposureGroup> => ({
  // displayName is optional in the generated type but mandatory for a group.
  title: group.displayName!,
  items: (
    await Promise.all(
      (group.items ?? []).map((item) => toAppExposureItem(item, appItem)),
    )
  ).filter((it): it is AppExposureItem => it !== null),
});

const buildEditorExposureForApp = async (
  app: EditorClientSpecMapEntry,
  documentServices: DocumentServicesObject,
  type: GroupType,
  currentEditor: EditorSubType | undefined,
  context: PlatformContext,
  httpClient: IHttpClient,
  currentBreakpoint: Breakpoint,
): Promise<AppExposure | null> => {
  // Read the app's CSM components once; used both to find the exposure
  // instances and, per item, to resolve custom-element / ERC metadata.
  const components =
    documentServices.platform.getAppDataByAppDefId(app.appDefinitionId)
      ?.components ?? [];
  const exposure = selectExposureForEditor(
    readEditorExposureInstances(components),
    currentEditor,
  );
  if (!exposure?.groups?.length) {
    return null;
  }

  const appItem: AppItemContext = {
    components,
    context,
    httpClient,
    currentBreakpoint,
  };
  const groups = (
    await Promise.all(
      exposure.groups
        .filter((g) => g.type === type)
        .map((g) => toAppExposureGroup(g, appItem)),
    )
  ).filter((g) => g.items.length > 0);

  if (groups.length === 0) {
    return null;
  }

  return {
    appDefId: app.appDefinitionId,
    title: app.appDefinitionName,
    groups,
  };
};

/**
 * Builds AppExposure[] for a given group type across all installed apps,
 * selecting — per app — the EditorExposure instance that applies to the
 * current editor (specific target wins, else the all-editors instance).
 */
export const buildAppExposures = async (
  documentServices: DocumentServicesObject,
  type: GroupType,
  context: PlatformContext,
): Promise<AppExposure[]> => {
  const currentEditor = getEditorSubType();
  const currentBreakpoint = getCurrentBreakpoint(documentServices, context);
  const httpClient = new HttpClient();
  const exposures = await Promise.all(
    documentServices.platform
      .getInstalledEditorApps()
      .map((app: EditorClientSpecMapEntry) =>
        buildEditorExposureForApp(
          app,
          documentServices,
          type,
          currentEditor,
          context,
          httpClient,
          currentBreakpoint,
        ),
      ),
  );
  return exposures.filter(
    (exposure): exposure is AppExposure => exposure !== null,
  );
};
